/**
 * Phone number validation and formatting utilities for frontend
 * Simplified version of the backend utility for client-side validation
 */

export interface PhoneValidationResult {
  isValid: boolean;
  formatted?: string;
  country?: string;
  error?: string;
}

/**
 * Validates and formats a phone number for authentication
 * @param phoneNumber - Raw phone number input
 * @param defaultCountry - Default country code (default: 'US')
 * @returns PhoneValidationResult with validation status and formatted number
 */
export function validatePhoneNumber(
  phoneNumber: string,
  defaultCountry: string = 'US'
): PhoneValidationResult {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return {
      isValid: false,
      error: 'Phone number is required'
    };
  }

  // Remove all non-digit characters except + at the beginning
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  if (cleaned.length === 0) {
    return {
      isValid: false,
      error: 'Phone number must contain digits'
    };
  }

  // Check if it starts with + (international format)
  if (cleaned.startsWith('+')) {
    return validateInternationalNumber(cleaned);
  }

  // Handle US numbers without country code
  if (defaultCountry === 'US') {
    return validateUSNumber(cleaned);
  }

  return {
    isValid: false,
    error: 'Invalid phone number format'
  };
}

/**
 * Validates international phone numbers (starting with +)
 */
function validateInternationalNumber(phoneNumber: string): PhoneValidationResult {
  // Basic international number validation
  // Should be + followed by 7-15 digits
  const internationalRegex = /^\+[1-9]\d{6,14}$/;
  
  if (!internationalRegex.test(phoneNumber)) {
    return {
      isValid: false,
      error: 'Invalid international phone number format. Use +[country code][number]'
    };
  }

  return {
    isValid: true,
    formatted: phoneNumber,
    country: 'International'
  };
}

/**
 * Validates US phone numbers
 */
function validateUSNumber(phoneNumber: string): PhoneValidationResult {
  // US numbers should be 10 digits
  if (phoneNumber.length === 10) {
    // Format as +1XXXXXXXXXX for Cognito
    const formatted = `+1${phoneNumber}`;
    return {
      isValid: true,
      formatted,
      country: 'US'
    };
  }

  // Handle 11-digit numbers starting with 1
  if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
    const formatted = `+${phoneNumber}`;
    return {
      isValid: true,
      formatted,
      country: 'US'
    };
  }

  return {
    isValid: false,
    error: 'US phone numbers must be 10 digits or 11 digits starting with 1'
  };
}

/**
 * Formats phone number for display purposes
 * @param phoneNumber - E.164 formatted phone number
 * @returns Human-readable formatted phone number
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
  if (!phoneNumber || !phoneNumber.startsWith('+')) {
    return phoneNumber;
  }

  // US/Canada numbers
  if (phoneNumber.startsWith('+1') && phoneNumber.length === 12) {
    const digits = phoneNumber.substring(2);
    return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }

  // Other international numbers - just add spaces
  return phoneNumber.replace(/(\d{1,3})(\d{3,4})/g, '$1 $2').trim();
}

/**
 * Checks if a phone number is in valid E.164 format
 * @param phoneNumber - Phone number to check
 * @returns boolean indicating if the number is valid E.164 format
 */
export function isValidE164(phoneNumber: string): boolean {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return false;
  }

  // E.164 format: + followed by up to 15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phoneNumber);
}

/**
 * Normalizes phone number input for consistent processing
 * @param phoneNumber - Raw phone number input
 * @returns Normalized phone number or null if invalid
 */
export function normalizePhoneNumber(phoneNumber: string): string | null {
  const validation = validatePhoneNumber(phoneNumber);
  return validation.isValid ? validation.formatted! : null;
}